import type { UploadLayout, UploadLookup } from '@erp/rpc';

/** With a single supplier on offer there is nothing to decide. */
export function defaultSupplierId(
  layouts: UploadLayout[],
  ready: boolean,
): string | null {
  return ready && layouts.length === 1 ? (layouts[0]?.id ?? null) : null;
}

export interface AssignedFiles {
  results: File | null;
  /** The supplier's second file, when its layout reads one. */
  lookup: File | null;
}

/**
 * Which dropped file is the results workbook and which the supplier's second
 * file. A file named after the second file's label (e.g. "QO - Feedback.xlsx"
 * for Feedback) is taken as it; otherwise the first file dropped is the
 * results. `swapped` lets the user flip a wrong guess.
 */
export function assignFiles(
  files: File[],
  lookup: UploadLookup | null,
  swapped: boolean,
): AssignedFiles {
  const [first = null, second = null] = files;
  if (!lookup || !second || !first) return { results: first, lookup: null };
  const label = lookup.label.toLowerCase();
  const firstIsLookup =
    first.name.toLowerCase().includes(label) &&
    !second.name.toLowerCase().includes(label);
  const lookupFirst = firstIsLookup !== swapped;
  return lookupFirst
    ? { results: second, lookup: first }
    : { results: first, lookup: second };
}

/**
 * What the supplier dialog needs for the chosen supplier: its second file, if
 * its layout reads one, and whether Convert can go ahead.
 */
export function supplierChoiceState(input: {
  layouts: UploadLayout[];
  layoutId: string | null;
  /** The second file: dropped with the results, or in the dialog. */
  lookupFile: File | null;
  ready: boolean;
}): { needsLookup: UploadLookup | null; canConvert: boolean } {
  const chosen = input.layouts.find(({ id }) => id === input.layoutId);
  const needsLookup = chosen?.definition.lookup ?? null;
  const canConvert =
    input.ready &&
    chosen !== undefined &&
    (needsLookup === null || input.lookupFile !== null);
  return { needsLookup, canConvert };
}

/** The chosen supplier's second-file settings, if its layout reads one. */
export function lookupOf(
  layouts: UploadLayout[],
  layoutId: string | null,
): UploadLookup | null {
  return layouts.find(({ id }) => id === layoutId)?.definition.lookup ?? null;
}
